/**
 * Tank Mapper Functions
 *
 * API ↔ Domain ↔ View 간 변환 로직
 */

import type {
  TankApiDto,
  TankDetailApiDto,
  CreateTankApiDto,
  StockFryApiDto,
  EmptyTankApiDto,
  GradeApiDto,
  DailyRecordItemApiDto,
} from '../api/tank.api'

import type {
  Tank,
  TankWithSchool,
  SchoolSummary,
  CreateTankCommand,
  StockFryCommand,
  EmptyTankCommand,
  GradeCommand,
  DailyRecord,
  UpsertDailyRecordsCommand,
} from '../domain/tank'

import type { SelectableTank, TankSubmitData } from '../view/tank.view'

// ============================================
// API → Domain
// ============================================

/**
 * API Tank DTO → Domain Tank
 */
export function toDomainTank(apiTank: TankApiDto): Tank {
  if (!apiTank) {
    throw new Error('apiTank is undefined or null')
  }
  return {
    id: apiTank.id,
    name: apiTank.name,
    aquafarmId: '', // API에 없음 - 별도 로드 필요
    volume: apiTank.volume,
    radius: apiTank.radius,
    depth: apiTank.depth,
    maxDensity: apiTank.max_density, // snake_case → camelCase
    location: apiTank.memo || '', // memo를 location으로 매핑
    memo: apiTank.memo,
    schoolId: null, // TankApiDto에는 schoolId 없음
    cameraId: null, // API에 camera 정보 없음
  }
}

/**
 * API Tank Detail DTO → Domain TankWithSchool
 */
export function toDomainTankWithSchool(apiTank: TankDetailApiDto): TankWithSchool {
  const baseTank = toDomainTank(apiTank)

  // School이 있을 때만 School 객체 생성
  if (apiTank.school && apiTank.school.id) {
    const school: SchoolSummary = {
      id: apiTank.school.id,
      name: apiTank.school.name,
      count: apiTank.school.count,
      weightPerFish: apiTank.school.weight_per_fish, // snake_case → camelCase
      startDate: parseApiDate(apiTank.school.start_date), // string → Date
    }

    return {
      ...baseTank,
      schoolId: apiTank.school.id,
      school,
    }
  }

  // School이 없으면 school 없이 반환
  return {
    ...baseTank,
    schoolId: null,
  }
}

/**
 * API Tank 배열 → Domain Tank 배열
 */
export function toDomainTanks(apiTanks: TankApiDto[]): Tank[] {
  return apiTanks.map(toDomainTank)
}

// ============================================
// Domain → API (Commands)
// ============================================

/**
 * Domain CreateTankCommand → API Request
 */
export function toApiCreateTank(command: CreateTankCommand): CreateTankApiDto {
  return {
    aquafam_id: command.aquafarmId, // typo in API - aquafam_id
    name: command.name,
    volume: command.volume,
    radius: command.radius,
    depth: command.depth,
    max_density: command.maxDensity, // camelCase → snake_case
    memo: command.location || command.memo || '', // location 또는 memo
  }
}

/**
 * Domain StockFryCommand → API Request
 * Note: tank_id is passed as path parameter, not in body
 */
export function toApiStockFry(command: StockFryCommand): StockFryApiDto {
  return {
    batch_info: {
      id: command.resolvedBatchId,
      name: command.resolvedBatchName,
    },
    school_info: {
      start_date: formatDateToISO(command.startDate),
      name: 'school',
      count: command.count,
      sample_weight: command.sampleWeight,
    },
  }
}

/**
 * Date 객체 → YYYY-MM-DD 형식 문자열
 *
 * API start_date는 YYYY-MM-DD 형식 요구
 * Note: toISOString()은 UTC로 변환되어 시간대 차이로 날짜가 하루 밀릴 수 있음
 * 로컬 시간대 기준으로 날짜를 포맷팅
 */
function formatDateToISO(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/**
 * Domain EmptyTankCommand → API Request
 * Note: tank_id is passed as path parameter, not in body
 */
export function toApiEmptyTank(command: EmptyTankCommand): EmptyTankApiDto {
  return {
    reason: command.reason,
  }
}

/**
 * Domain GradeCommand → API Request
 */
export function toApiGrade(command: GradeCommand): GradeApiDto {
  return {
    move_date: formatDateToYYYYMMDD(command.moveDate), // Date → YYYYMMDD
    grade_cases: command.cases.map((gradeCase) => ({
      departure_tank_data: {
        id: gradeCase.departure.tankId,
        subtracted_count: gradeCase.departure.subtractedCount,
      },
      arrival_tank_datas: gradeCase.arrivals.map((arrival) => ({
        id: arrival.tankId,
        count: arrival.count,
        sample_weight: arrival.sampleWeight,
      })),
    })),
  }
}

/**
 * Domain UpsertDailyRecordsCommand → API Request
 */
export function toApiUpsertDailyRecords(
  command: UpsertDailyRecordsCommand
): DailyRecordItemApiDto[] {
  return command.records.map((record) => ({
    tank_id: record.tankId,
    date: formatDateToYYYYMMDD(record.date), // Date → YYYYMMDD
    nh3: record.nh3,
    no2: record.no2,
    sample_weight: record.sampleWeight,
    stock_count: record.stockCount,
    mortality_count: record.mortalityCount,
    harvest_count: record.harvestCount,
    manual_amount: record.manualAmount, // 신규 필드
    manual_count: record.manualCount, // 신규 필드
  }))
}

// ============================================
// Domain → View
// ============================================

/**
 * Domain Tank → View SelectableTank
 */
export function toSelectableTank(
  tank: Tank,
  groupId: string,
  groupName: string,
  isSelected: boolean
): SelectableTank {
  return {
    id: tank.id,
    name: tank.name,
    groupId,
    groupName,
    isSelected,
    isDisabled: !tank.schoolId, // School이 없으면 비활성화
    schoolId: tank.schoolId,
  }
}

/**
 * Domain Tank → View TankSubmitData
 */
export function toTankSubmitData(
  tank: Tank,
  groupId: string,
  groupName: string
): TankSubmitData {
  return {
    tankId: tank.id,
    tankName: tank.name,
    groupId,
    groupName,
  }
}

// ============================================
// Utility Functions
// ============================================

/**
 * API Date String → Date 객체
 *
 * API는 ISO 8601 형식 (YYYY-MM-DD) 반환
 */
function parseApiDate(dateString: string): Date {
  return new Date(dateString)
}

/**
 * Date 객체 → YYYYMMDD 형식 문자열
 *
 * API는 YYYYMMDD 형식 요구
 */
function formatDateToYYYYMMDD(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}${month}${day}`
}

/**
 * YYYYMMDD 문자열 → Date 객체
 */
export function parseYYYYMMDD(dateString: string): Date {
  const year = parseInt(dateString.substring(0, 4))
  const month = parseInt(dateString.substring(4, 6)) - 1
  const day = parseInt(dateString.substring(6, 8))
  return new Date(year, month, day)
}
